import { Check, Square } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const RatingDropdown = ({
  selectedValue, // current single selected rating (string)
  onChange, // function to call when user selects a rating
}) => {
  const [isOpen, setIsOpen] = useState(true); // Auto open when component mounts
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Internal rating options - no need to pass from parent
  const options = [
    { id: null, label: "Без рейтинга" },
    { id: "1", label: "1 Звезда" },
    { id: "2", label: "2 Звезды" },
    { id: "3", label: "3 Звезды" },
    { id: "4", label: "4 Звезды" },
    { id: "5", label: "5 Звезд" },
  ];

  // Store the position of the dropdown (absolute positioning)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Close the dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Compute the position (top, left) for the dropdown when component mounts
  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  // Handle user selection of a rating
  const handleSelect = (id) => {
    if (onChange) {
      onChange(id); // pass the single rating string to parent
    }
    setIsOpen(false);
  };

  // The dropdown menu content; we render it via a React Portal
  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      style={{
        position: "absolute",
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: 220,
        zIndex: 1000,
      }}
    >
      <div className="rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
        {/* List of rating options */}
        <div className="max-h-64 overflow-auto p-2">
          {options.map(({ id, label }) => {
            const isSelected = selectedValue === Number(id);
            return (
              <label
              key={id || "no-rating"}
              className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 px-2 rounded"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => handleSelect(id)}
              >
              {/* Group for Square icon and label text */}
              <div className="flex items-center space-x-2">
                <Square
                stroke="#0832DE"
                width={16}
                height={16}
                strokeWidth={2}
                />
                <span className="text-gray-700 text-sm">{label}</span>
              </div>

              {/* Conditionally render Check icon on the right */}
              {isSelected && (
                <Check
                width={16}
                height={16}
                strokeWidth={2.5}
                stroke="#2B3674"
                />
              )}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Hidden container for positioning reference */}
      <div ref={containerRef} className="w-full h-full" />

      {/* Render the dropdown content in a portal */}
      {createPortal(dropdownContent, document.body)}
    </>
  );
};

export default RatingDropdown;
